import ApiService from './apiService';

/**
 * Service pour gérer les opérations liées aux produits
 */
class ProductsService {
    /**
     * Récupérer la liste des produits avec filtres et pagination
     * @param {Object} params Paramètres de filtrage et pagination
     * @returns {Promise} Réponse de l'API avec la liste des produits
     */
    getProducts(params = {}) {
        return ApiService.get('/products', params);
    }

    /**
     * Récupérer un produit par son ID
     * @param {Number} id ID du produit
     * @param {Object} params Paramètres additionnels (par exemple, relations à charger)
     * @returns {Promise} Réponse de l'API avec les détails du produit
     */
    getProduct(id, params = {}) {
        return ApiService.get(`/products/${id}`, params);
    }

    /**
     * Créer un nouveau produit
     * @param {Object} productData Données du produit à créer
     * @returns {Promise} Réponse de l'API avec le produit créé
     */
    createProduct(productData) {
        return ApiService.post('/products', productData);
    }

    /**
     * Mettre à jour un produit existant
     * @param {Number} id ID du produit à mettre à jour
     * @param {Object} productData Nouvelles données du produit
     * @returns {Promise} Réponse de l'API avec le produit mis à jour
     */
    updateProduct(id, productData) {
        return ApiService.put(`/products/${id}`, productData);
    }

    /**
     * Supprimer un produit
     * @param {Number} id ID du produit à supprimer
     * @returns {Promise} Réponse de l'API
     */
    deleteProduct(id) {
        return ApiService.delete(`/products/${id}`);
    }

    /**
     * Télécharger une image pour un produit
     * @param {Number} id ID du produit
     * @param {FormData} formData Données du formulaire contenant l'image
     * @param {Function} progressCallback Fonction de callback pour suivre la progression
     * @returns {Promise} Réponse de l'API avec l'URL de l'image téléchargée
     */
    uploadProductImage(id, formData, progressCallback = null) {
        return ApiService.upload(`/products/${id}/image`, formData, progressCallback);
    }

    /**
     * Supprimer l'image d'un produit
     * @param {Number} id ID du produit
     * @returns {Promise} Réponse de l'API
     */
    deleteProductImage(id) {
        return ApiService.delete(`/products/${id}/image`);
    }

    /**
     * Récupérer les produits à faible stock
     * @param {Number} threshold Seuil de stock minimum
     * @returns {Promise} Réponse de l'API avec les produits à faible stock
     */
    getLowStockProducts(threshold = 5) {
        return ApiService.get('/products', { low_stock: true, threshold });
    }

    /**
     * Rechercher des produits
     * @param {String} query Terme de recherche
     * @param {Object} options Options additionnelles (catégorie, etc.)
     * @returns {Promise} Réponse de l'API avec les résultats de recherche
     */
    searchProducts(query, options = {}) {
        return ApiService.get('/products', {
            search: query,
            ...options
        });
    }

    /**
     * Obtenir les produits par catégorie
     * @param {Number} categoryId ID de la catégorie
     * @param {Object} params Paramètres additionnels
     * @returns {Promise} Réponse de l'API avec les produits de la catégorie
     */
    getProductsByCategory(categoryId, params = {}) {
        return ApiService.get('/products', {
            category_id: categoryId,
            ...params
        });
    }

    /**
     * Mettre à jour le stock d'un produit
     * @param {Number} id ID du produit
     * @param {Number} quantity Nouvelle quantité en stock
     * @returns {Promise} Réponse de l'API avec le produit mis à jour
     */
    updateProductStock(id, quantity) {
        return ApiService.put(`/products/${id}/stock`, { quantity });
    }

    /**
     * Obtenir les statistiques des produits (ventes, popularité, etc.)
     * @returns {Promise} Réponse de l'API avec les statistiques
     */
    getProductsStats() {
        return ApiService.get('/products/stats');
    }

    /**
 * Récupérer les ventes d'un produit
 * @param {Number} productId ID du produit
 * @param {Object} params Paramètres additionnels
 * @returns {Promise} Réponse de l'API avec les ventes du produit
 */
    getProductSales(productId, params = {}) {
        return ApiService.get(`/products/${productId}/sales`, params);
    }

    /**
     * Récupérer les informations de stock d'un produit
     * @param {Number} productId ID du produit
     * @returns {Promise} Réponse de l'API avec les informations de stock
     */
    getProductStock(productId) {
        return ApiService.get(`/products/${productId}/stock`);
    }

    /**
     * Récupérer les transactions d'inventaire d'un produit
     * @param {Number} productId ID du produit
     * @param {Object} params Paramètres additionnels (pagination, etc.)
     * @returns {Promise} Réponse de l'API avec les transactions du produit
     */
    getProductTransactions(productId, params = {}) {
        return ApiService.get(`/inventory/products/${productId}/transactions`, params);
    }

    /**
     * Formater un produit pour l'affichage
     * @param {Object} product Données du produit
     * @returns {Object} Produit formaté avec informations additionnelles
     */
    formatProductForDisplay(product) {
        if (!product) return null;

        return {
            ...product,
            formattedPrice: this.formatPrice(product.price_incl_tax),
            formattedPriceHT: this.formatPrice(product.price_excl_tax),
            stockStatus: this.getStockStatus(product.stock)
        };
    }

    /**
     * Formater un prix en devise
     * @param {Number} price Montant à formater
     * @returns {String} Prix formaté
     */
    formatPrice(price) {
        if (price === undefined || price === null) return '';

        return new Intl.NumberFormat('fr-FR', {
            style: 'currency',
            currency: 'XOF',
            minimumFractionDigits: 0
        }).format(price);
    }

    /**
     * Obtenir le statut du stock
     * @param {Number} quantity Quantité en stock
     * @returns {Object} Statut du stock (texte et classe CSS)
     */
    getStockStatus(quantity) {
        if (quantity <= 0) {
            return { text: 'Rupture de stock', class: 'out-of-stock' };
        } else if (quantity <= 5) {
            return { text: 'Stock faible', class: 'low-stock' };
        } else {
            return { text: 'En stock', class: 'in-stock' };
        }
    }
}

export default new ProductsService();